/**
 * The product's icons, in one family: a 24-unit grid, a 1.75-unit round
 * stroke, `currentColor`, no fills. They are hidden from assistive technology —
 * the control an icon sits in carries the words — and they are never used as
 * decoration on their own.
 */
const Svg = ({ size, children }: { size: number; children: React.ReactNode }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        {children}
    </svg>
);

type IconProps = { size?: number };

export const IconChevronRight = ({ size = 14 }: IconProps) => <Svg size={size}><path d="m9 6 6 6-6 6" /></Svg>;
export const IconChevronLeft = ({ size = 14 }: IconProps) => <Svg size={size}><path d="m15 6-6 6 6 6" /></Svg>;
export const IconChevronDown = ({ size = 14 }: IconProps) => <Svg size={size}><path d="m6 9 6 6 6-6" /></Svg>;
export const IconArrowLeft = ({ size = 16 }: IconProps) => <Svg size={size}><path d="M19 12H5M11 18l-6-6 6-6" /></Svg>;
export const IconArrowRight = ({ size = 16 }: IconProps) => <Svg size={size}><path d="M5 12h14M13 6l6 6-6 6" /></Svg>;

export const IconClose = ({ size = 18 }: IconProps) => <Svg size={size}><path d="M6 6l12 12M18 6 6 18" /></Svg>;
export const IconMenu = ({ size = 20 }: IconProps) => <Svg size={size}><path d="M4 7h16M4 12h16M4 17h16" /></Svg>;
export const IconCheck = ({ size = 14 }: IconProps) => <Svg size={size}><path d="m5 12.5 4.5 4.5L19 7.5" /></Svg>;
export const IconPlus = ({ size = 16 }: IconProps) => <Svg size={size}><path d="M12 5v14M5 12h14" /></Svg>;
export const IconSearch = ({ size = 16 }: IconProps) => <Svg size={size}><circle cx="11" cy="11" r="6.5" /><path d="m20 20-4.2-4.2" /></Svg>;
export const IconDownload = ({ size = 16 }: IconProps) => <Svg size={size}><path d="M12 4v11M7 10l5 5 5-5M5 20h14" /></Svg>;
export const IconUser = ({ size = 16 }: IconProps) => <Svg size={size}><circle cx="12" cy="8" r="3.5" /><path d="M5 20c.8-3.6 3.6-5.5 7-5.5s6.2 1.9 7 5.5" /></Svg>;
export const IconMore = ({ size = 20 }: IconProps) => <Svg size={size}><path d="M5 12h.01M12 12h.01M19 12h.01" strokeWidth="2.5" /></Svg>;

export const IconCalendar = ({ size = 16 }: IconProps) => (
    <Svg size={size}><rect x="3.5" y="5" width="17" height="15.5" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" /></Svg>
);
export const IconPhone = ({ size = 16 }: IconProps) => (
    <Svg size={size}><path d="M6.6 3.5h2.6l1.4 4.1-2 1.4a12.6 12.6 0 0 0 6.4 6.4l1.4-2 4.1 1.4v2.6A2.1 2.1 0 0 1 18.4 20 15 15 0 0 1 4 5.6a2.1 2.1 0 0 1 2.1-2.1Z" /></Svg>
);
export const IconMail = ({ size = 16 }: IconProps) => (
    <Svg size={size}><rect x="3.5" y="5.5" width="17" height="13" rx="2" /><path d="m4 7 8 6 8-6" /></Svg>
);
export const IconMessage = ({ size = 16 }: IconProps) => (
    <Svg size={size}><path d="M20 12a8 8 0 0 1-11.6 7.1L4 20l.9-4.2A8 8 0 1 1 20 12Z" /></Svg>
);
export const IconCopy = ({ size = 16 }: IconProps) => (
    <Svg size={size}><rect x="8.5" y="8.5" width="11" height="11" rx="2" /><path d="M15.5 8.5V6.5a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2" /></Svg>
);

/* the places */
export const IconToday = ({ size = 18 }: IconProps) => (
    <Svg size={size}><path d="M4 13.5 6.8 5.7A2 2 0 0 1 8.7 4.5h6.6a2 2 0 0 1 1.9 1.2L20 13.5V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4.5Z" /><path d="M4 13.5h4.5l1.5 2.5h4l1.5-2.5H20" /></Svg>
);
export const IconCustomers = ({ size = 18 }: IconProps) => (
    <Svg size={size}><circle cx="9" cy="8.5" r="3.5" /><path d="M3 19.5a6 6 0 0 1 12 0" /><path d="M16 5.2a3.5 3.5 0 0 1 0 6.6M21 19.5a6 6 0 0 0-3.5-5.4" /></Svg>
);
export const IconCheques = ({ size = 18 }: IconProps) => (
    <Svg size={size}><rect x="2.5" y="6" width="19" height="12" rx="2" /><path d="M6 14.5h5M15 14.5h3M6 10h12" /></Svg>
);
export const IconReports = ({ size = 18 }: IconProps) => (
    <Svg size={size}><path d="M4 20h16" /><path d="M7 16v-5M12 16V7M17 16v-8" /></Svg>
);
export const IconStock = ({ size = 18 }: IconProps) => (
    <Svg size={size}><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" /><path d="m4 7.5 8 4.5 8-4.5M12 12v9" /></Svg>
);
export const IconSettings = ({ size = 18 }: IconProps) => (
    <Svg size={size}><path d="M4 7h9M17 7h3M4 17h3M11 17h9" /><circle cx="15" cy="7" r="2" /><circle cx="9" cy="17" r="2" /></Svg>
);
export const IconSignOut = ({ size = 18 }: IconProps) => (
    <Svg size={size}><path d="M9 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h3M16 16l4-4-4-4M20 12H9" /></Svg>
);

/* appearance */
export const IconSun = ({ size = 16 }: IconProps) => (
    <Svg size={size}><circle cx="12" cy="12" r="4" /><path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" /></Svg>
);
export const IconMoon = ({ size = 16 }: IconProps) => (
    <Svg size={size}><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" /></Svg>
);
export const IconMonitor = ({ size = 16 }: IconProps) => (
    <Svg size={size}><rect x="3" y="4.5" width="18" height="12" rx="2" /><path d="M9 20h6M12 16.5V20" /></Svg>
);

/* trust and connection (the site, the data source) */
export const IconShield = ({ size = 18 }: IconProps) => (
    <Svg size={size}><path d="M12 3.5 5 6.2v5.4c0 4.3 2.9 7.6 7 8.9 4.1-1.3 7-4.6 7-8.9V6.2L12 3.5Z" /><path d="m9 12 2.2 2.2L15.5 10" /></Svg>
);
export const IconLock = ({ size = 18 }: IconProps) => (
    <Svg size={size}><rect x="5" y="10.5" width="14" height="10" rx="2" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" /></Svg>
);
export const IconPlug = ({ size = 18 }: IconProps) => (
    <Svg size={size}><path d="M9 3.5V8M15 3.5V8M6.5 8h11v3a5.5 5.5 0 0 1-11 0V8ZM12 16.5V21" /></Svg>
);
export const IconRefresh = ({ size = 16 }: IconProps) => (
    <Svg size={size}><path d="M19.5 12a7.5 7.5 0 0 1-13 5.1M4.5 12a7.5 7.5 0 0 1 13-5.1" /><path d="M17.5 3.5v3.4h-3.4M6.5 20.5v-3.4h3.4" /></Svg>
);
export const IconSpark = ({ size = 16 }: IconProps) => (
    <Svg size={size}><path d="M12 4v4M12 16v4M4 12h4M16 12h4M7 7l1.8 1.8M15.2 15.2 17 17M7 17l1.8-1.8M15.2 8.8 17 7" /></Svg>
);
export const IconLedger = ({ size = 18 }: IconProps) => (
    <Svg size={size}><rect x="4.5" y="3.5" width="15" height="17" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></Svg>
);

/* feedback and the site */
export const IconInfo = ({ size = 16 }: IconProps) => (
    <Svg size={size}><circle cx="12" cy="12" r="8.5" /><path d="M12 11v5.5M12 7.6h.01" /></Svg>
);
export const IconAlert = ({ size = 16 }: IconProps) => (
    <Svg size={size}><path d="M10.3 4.3 2.9 17.2A2 2 0 0 0 4.6 20h14.8a2 2 0 0 0 1.7-2.8L13.7 4.3a2 2 0 0 0-3.4 0Z" /><path d="M12 9.5v4.5M12 17h.01" /></Svg>
);
export const IconExternal = ({ size = 14 }: IconProps) => (
    <Svg size={size}><path d="M14 4h6v6M20 4l-9 9M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" /></Svg>
);
export const IconWindows = ({ size = 16 }: IconProps) => (
    <Svg size={size}><rect x="3.5" y="4.5" width="17" height="12" rx="1.5" /><path d="M9 20h6M12 16.5V20" /></Svg>
);
export const IconFlow = ({ size = 16 }: IconProps) => (
    <Svg size={size}><circle cx="5.5" cy="12" r="2" /><circle cx="18.5" cy="12" r="2" /><path d="M7.5 12h9M14 9l2.5 3-2.5 3" /></Svg>
);
export const IconTeam = ({ size = 18 }: IconProps) => (
    <Svg size={size}><circle cx="8" cy="9" r="3" /><circle cx="16.5" cy="10" r="2.5" /><path d="M2.5 19a5.5 5.5 0 0 1 11 0M13.5 18.5a4.5 4.5 0 0 1 8 0" /></Svg>
);
